import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '@/components/site/Header';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';

const BREADCRUMB = [
  { name: 'Start', path: '/' },
  { name: 'Datenschutz', path: '/datenschutz' },
];

const Section = ({ title, children, id }) => (
  <section id={id} className="py-8 border-t border-slate-200/70">
    <h2 className="font-display font-black text-slate-900 text-xl tracking-tight mb-4">{title}</h2>
    <div className="text-slate-700 text-[15px] leading-relaxed space-y-3">{children}</div>
  </section>
);

const SubH = ({ children }) => (
  <h3 className="font-display font-bold text-slate-900 text-base mt-4 mb-1">{children}</h3>
);

export default function Datenschutz() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-[#f6f7f9] text-slate-900 min-h-screen" data-testid="datenschutz-page">
      <Seo
        title="Datenschutzerklärung"
        description="Datenschutzerklärung der MIBAX Messtechnik e.K. gemäß DSGVO Art. 13/14 und § 25 TDDDG. Cookie-Einwilligung, self-hosted Schriftarten, LinkedIn Insight Tag und PostHog nur nach Zustimmung."
        path="/datenschutz"
        breadcrumb={BREADCRUMB}
      />
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#0891b2] mb-8" data-testid="datenschutz-back-link">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>

          <div className="eyebrow mb-5">Rechtliche Hinweise</div>
          <h1 className="font-display font-black text-slate-900 tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[1.05] mb-3 break-words hyphens-auto" lang="de">
            Datenschutzerklärung
          </h1>
          <p className="text-slate-500 text-base">
            Information gemäß Art. 13, 14 DSGVO sowie § 25 TDDDG. Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}.
          </p>

          <Section title="1. Verantwortlicher">
            <p>
              Verantwortlicher im Sinne der DSGVO und anderer nationaler Datenschutzgesetze ist:
            </p>
            <p>
              MIBAX Messtechnik – Inhaber: M. Basic<br />
              Tal 44, 80331 München, Deutschland<br />
              Telefon: <a href="tel:+436769218022" className="text-[#0891b2] hover:underline">+43 676 9218022</a><br />
              E-Mail: <a href="mailto:M.Basic@mibax-messtechnik.com" className="text-[#0891b2] hover:underline break-all">M.Basic@mibax-messtechnik.com</a>
            </p>
          </Section>

          <Section title="2. Allgemeine Hinweise zur Datenverarbeitung">
            <p>
              Wir verarbeiten personenbezogene Daten unserer Nutzer grundsätzlich nur, soweit dies zur
              Bereitstellung einer funktionsfähigen Website sowie unserer Inhalte und Leistungen
              erforderlich ist. Die Verarbeitung erfolgt regelmäßig nur nach Einwilligung des Nutzers
              oder auf einer der in Art. 6 Abs. 1 DSGVO genannten Rechtsgrundlagen.
            </p>
          </Section>

          <Section title="3. Bereitstellung der Website & Erstellung von Logfiles">
            <p>
              Bei jedem Aufruf unserer Website erfasst unser System automatisiert Daten und
              Informationen vom Computersystem des aufrufenden Rechners. Folgende Daten werden
              hierbei erhoben:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li>IP-Adresse (gekürzt / anonymisiert nach 7 Tagen)</li>
              <li>Datum und Uhrzeit des Zugriffs</li>
              <li>Verwendeter Browser-Typ und -Version</li>
              <li>Verwendetes Betriebssystem</li>
              <li>Referrer-URL (zuvor besuchte Seite)</li>
              <li>Aufgerufene URL und übertragene Datenmenge</li>
            </ul>
            <p>
              Rechtsgrundlage: Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an einer
              technisch fehlerfreien Darstellung und Sicherheit der Website).
              Speicherdauer: maximal 30 Tage, danach automatische Löschung.
            </p>
          </Section>

          <Section title="4. Cookies, lokale Speicherung & Einwilligung">
            <SubH>Technisch notwendige Speicherung (§ 25 Abs. 2 Nr. 2 TDDDG)</SubH>
            <p>
              Für den Betrieb dieser Website nutzen wir den lokalen Speicher (localStorage)
              Ihres Browsers, um Ihre Cookie-Einwilligung zu dokumentieren
              (Schlüssel: <code className="text-xs">mibax_consent_v1</code>). Diese
              Speicherung ist zwingend erforderlich, damit wir Ihnen den Consent-Banner
              nicht bei jedem Seitenaufruf erneut anzeigen. Eine Einwilligung ist hierfür
              nicht erforderlich.
            </p>

            <SubH>Marketing- und Analyse-Cookies (§ 25 Abs. 1 TDDDG)</SubH>
            <p>
              Nach ausdrücklicher Einwilligung setzen wir folgende Dienste ein, um
              die Reichweite unserer LinkedIn-Kampagnen zu messen und unsere Website
              zu verbessern. Ohne Ihre Einwilligung wird <strong>keiner</strong> dieser
              Dienste geladen und keine Daten übertragen:
            </p>
            <ul className="list-disc pl-6 space-y-3 text-slate-700 mt-3">
              <li>
                <strong>LinkedIn Insight Tag</strong> (Anbieter: LinkedIn Ireland Unlimited
                Company, Wilton Plaza, Wilton Place, Dublin 2, Irland) — Conversion-
                und Reichweiten-Messung unserer LinkedIn-Anzeigen. Übertragung von
                IP-Adresse, Referrer-URL und ggf. Geräteinformationen an LinkedIn.
                Rechtsgrundlage: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
                Datenschutzhinweise:{' '}
                <a
                  href="https://www.linkedin.com/legal/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#0891b2]"
                >
                  linkedin.com/legal/privacy-policy
                </a>
                .
              </li>
              <li>
                <strong>PostHog</strong> (Anbieter: PostHog Inc., 2261 Market Street #4008,
                San Francisco, CA 94114, USA) — Nutzungsanalyse und optionales
                Session-Recording zur Optimierung der Nutzerführung. Rechtsgrundlage:
                Art. 6 Abs. 1 lit. a DSGVO (Einwilligung).
                Datenschutzhinweise:{' '}
                <a
                  href="https://posthog.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline text-[#0891b2]"
                >
                  posthog.com/privacy
                </a>
                .
              </li>
            </ul>

            <SubH>Widerruf Ihrer Einwilligung</SubH>
            <p>
              Sie können Ihre erteilte Einwilligung jederzeit mit Wirkung für die
              Zukunft widerrufen. Klicken Sie hierfür auf den Link{' '}
              <strong>„Cookie-Einstellungen"</strong> im Footer dieser Website. Die
              Rechtmäßigkeit der bis zum Widerruf erfolgten Verarbeitung bleibt vom
              Widerruf unberührt.
            </p>

            <SubH>Drittlandübermittlung (USA)</SubH>
            <p>
              LinkedIn und PostHog können personenbezogene Daten in die USA übermitteln.
              LinkedIn und PostHog haben sich dem EU-U.S. Data Privacy Framework
              unterworfen bzw. berufen sich auf Standardvertragsklauseln der EU-
              Kommission. Ein gleichwertiges Datenschutzniveau kann trotz dieser
              Maßnahmen nicht in allen Fällen garantiert werden.
            </p>
          </Section>

          <Section title="5. Kontaktformular & ROI-Rechner">
            <SubH>Welche Daten erheben wir?</SubH>
            <p>Über das Kontaktformular auf der Startseite erheben wir folgende Daten:</p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li>Name (Pflichtfeld)</li>
              <li>Firma (Pflichtfeld)</li>
              <li>E-Mail-Adresse (Pflichtfeld)</li>
              <li>Telefonnummer (optional)</li>
              <li>Anlagengröße / Kompressorleistung (optional)</li>
              <li>Freie Nachricht (optional)</li>
              <li>Zeitstempel der Einsendung</li>
            </ul>
            <SubH>Zweck der Verarbeitung</SubH>
            <p>
              Die Daten verwenden wir ausschließlich, um Ihre Anfrage zu beantworten, ein
              kostenloses Audit zu terminieren und ggf. den anschließenden Servicevertrag
              vorzubereiten. Eine Weitergabe an Dritte erfolgt nicht.
            </p>
            <SubH>Rechtsgrundlage</SubH>
            <p>
              Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen auf Anfrage der betroffenen
              Person) sowie Art. 6 Abs. 1 lit. f DSGVO (berechtigtes Interesse an effizienter
              B2B-Kommunikation).
            </p>
            <SubH>Speicherdauer</SubH>
            <p>
              Anfragen werden gelöscht, sobald sie für die Bearbeitung nicht mehr erforderlich
              sind, spätestens nach Ablauf der gesetzlichen Aufbewahrungsfristen (i. d. R. 6 Jahre
              gem. § 257 HGB / 10 Jahre gem. § 147 AO bei Geschäftsbriefen).
            </p>
            <SubH>ROI-Rechner</SubH>
            <p>
              Der ROI-Rechner verarbeitet die von Ihnen eingegebenen Werte (Betriebsstunden,
              Leckanzahl, Strompreis, Druck) ausschließlich für die Berechnung. Eine dauerhafte
              Speicherung dieser Eingaben findet nicht statt.
            </p>
          </Section>

          <Section title="6. Hosting & technische Dienstleister">
            <p>
              Die Website wird auf Servern in der Europäischen Union gehostet. Der Hosting-Anbieter
              verarbeitet personenbezogene Daten in unserem Auftrag (Art. 28 DSGVO – Auftragsverarbeitung).
            </p>
            <p>
              Die im Kontaktformular eingegebenen Daten werden in einer MongoDB-Datenbank gespeichert.
              Der Datenbankserver befindet sich in der EU.
            </p>
          </Section>

          <Section title="7. Schriftarten – selbst gehostet">
            <p>
              Diese Website nutzt zur einheitlichen Darstellung von Schriftarten die Open-Source-Schriften
              <strong> Chivo</strong>, <strong>IBM Plex Sans</strong> und <strong>JetBrains Mono</strong>.
              Die Schriftdateien werden <strong>ausschließlich von unserem eigenen Server</strong> ausgeliefert
              (Self-Hosting via <code>@fontsource</code>). Es besteht <strong>keine Verbindung zu Google-Servern</strong>
              oder zu sonstigen externen Schrift-CDNs. Eine Übermittlung Ihrer IP-Adresse an Dritte findet bei
              der Darstellung der Schriftarten somit nicht statt.
            </p>
          </Section>

          <Section title="8. SSL-/TLS-Verschlüsselung">
            <p>
              Diese Seite nutzt aus Gründen der Sicherheit und zum Schutz der Übertragung
              vertraulicher Inhalte eine SSL- bzw. TLS-Verschlüsselung. Eine verschlüsselte
              Verbindung erkennen Sie daran, dass die Adresszeile des Browsers von „http://" auf
              „https://" wechselt und an dem Schloss-Symbol in Ihrer Browserzeile.
            </p>
          </Section>

          <Section title="9. Ihre Rechte als betroffene Person">
            <p>Sie haben jederzeit folgende Rechte gegenüber dem Verantwortlichen:</p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700">
              <li><strong>Auskunft</strong> (Art. 15 DSGVO) über die zu Ihrer Person verarbeiteten Daten</li>
              <li><strong>Berichtigung</strong> (Art. 16 DSGVO) unrichtiger Daten</li>
              <li><strong>Löschung</strong> (Art. 17 DSGVO) Ihrer Daten</li>
              <li><strong>Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)</li>
              <li><strong>Widerspruch</strong> gegen die Verarbeitung (Art. 21 DSGVO)</li>
              <li><strong>Datenübertragbarkeit</strong> (Art. 20 DSGVO)</li>
              <li><strong>Widerruf einer Einwilligung</strong> mit Wirkung für die Zukunft (Art. 7 Abs. 3 DSGVO)</li>
            </ul>
            <p>
              Zur Geltendmachung dieser Rechte genügt eine formlose Mitteilung per E-Mail an{' '}
              <a href="mailto:M.Basic@mibax-messtechnik.com" className="text-[#0891b2] hover:underline break-all">
                M.Basic@mibax-messtechnik.com
              </a>
              .
            </p>
          </Section>

          <Section title="10. Beschwerderecht bei der Aufsichtsbehörde">
            <p>
              Sie haben das Recht, sich bei einer Datenschutzaufsichtsbehörde zu beschweren
              (Art. 77 DSGVO). Zuständige Behörde für unseren Sitz in München ist:
            </p>
            <p>
              Bayerisches Landesamt für Datenschutzaufsicht (BayLDA)<br />
              Promenade 18, 91522 Ansbach<br />
              <a href="https://www.lda.bayern.de" target="_blank" rel="noreferrer" className="text-[#0891b2] hover:underline">
                www.lda.bayern.de
              </a>
            </p>
          </Section>

          <Section title="11. Aktualität & Änderungen dieser Datenschutzerklärung">
            <p>
              Diese Datenschutzerklärung ist aktuell gültig. Durch die Weiterentwicklung unserer
              Website oder geänderter gesetzlicher Vorgaben kann es notwendig werden, diese
              Datenschutzerklärung anzupassen. Die jeweils aktuelle Fassung können Sie jederzeit
              auf dieser Seite abrufen.
            </p>
          </Section>

          <div className="py-10 mt-6 border-t border-slate-200/70 font-mono-tech text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Stand: {new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
